<template>
  <div
    :class="appendCls"
    @click.stop="toggleMenu"
    v-clickoutside="handleClose"
  >
    <zm-scrollbar
      ref="wrapper"
      class="zm-select-scrollbar-fix"
      :wrap-style="inputWrapStyl"
    >
      <div v-if="tree.filterable" ref="reference" class="zm-select-input">
        <input
          ref="referenceInner"
          class="zm-select-input__inner"
          v-model="query"
          autocomplete="off"
          :placeholder="currentPlaceholder"
          :label="selectedLabel"
          :tabindex="selectDisabled ? -1 : 0"
          :id="targetId"
          :disabled="selectDisabled"
          :aria-controls="listId"
          :aria-owns="listId"
          :aria-required="ariaRequired"
          :aria-label="(label ? (label + ',') : '') + (selectedLabel || currentPlaceholder)"
          :aria-describedby="labelBy"
          role="combobox"
          :aria-expanded="(visible).toString()"
          :aria-activedescendant="visible ? activedescendant: null"
          aria-haspopup="true"
          @input="debouncedQueryChange"
          @focus="handleFocus"
          @blur="handleBlur"
          @keydown="handleKeyDown"
        />
        <button
          tabindex="-1"
          class="zm-select-toggle"
          type="button"
          aria-hidden="true"
          @click.stop="handleIconClick"
          :aria-label="toggleText"
        >
          <i :class="['zm-select__caret', 'zm-input__icon', 'zm-icon-up']"></i>
        </button>
      </div>

      <div
        v-else
        ref="reference"
        class="zm-select-input"
        :style="inputStyl"
        :class="{ 'has-slot': $slots.prefix }"
      >
        <div class="zm-select-prefix" v-if="$slots.prefix">
          <slot name="prefix"></slot>
        </div>
        <span
          ref="referenceInner"
          role="button"
          class="zm-select-span__inner"
          :class="{'is-placeholder': selectedLabel===''}"
          :tabindex="selectDisabled ? -1 : 0"
          :id="targetId"
          :disabled="selectDisabled"
          :aria-expanded="(visible).toString()"
          aria-haspopup="listbox"
          :aria-controls="listId"
          :aria-owns="listId"
          :aria-required="ariaRequired"
          :aria-label="(label ? (label + ',') : '') + (selectedLabel || currentPlaceholder)"
          :aria-describedby="labelBy"
          @focus="handleFocus"
          @blur="handleBlur"
          @keydown="handleBtnKeyDown"
        >
          {{selectedLabel || currentPlaceholder}}
        </span>
        <div
          class="zm-select__tags"
          ref="tags"
          :style="{ 'max-width': inputWidth - 25 + 'px' }"
        >
          <span v-if="collapseTags && selected.length">
            <zm-tag
              :closable="!selectDisabled"
              class="zm-tag-fix"
              :size="collapseTagSize"
              :hit="selected[0].hitState"
              :delete-text="t('tag.delete') + ' ' + selected[0].currentLabel"
              type="info"
              @close="deleteTag($event, selected[0])"
              disable-transitions
            >
              <span class="zm-select__tags-text">{{ selected[0].currentLabel }}
              </span>
            </zm-tag>
            <zm-tag
              class="zm-tag-fix"
              v-if="selected.length > 1"
              :closable="false"
              :size="collapseTagSize"
              type="info"
              disable-transitions
            >
              <span class="zm-select__tags-text">+ {{ selected.length - 1 }}
              </span>
            </zm-tag>
          </span>
          <div v-if="!collapseTags">
            <zm-tag
              v-for="item in selected"
              :key="getValueKey(item)"
              :delete-text="t('tag.delete') + ' '+ item.currentLabel"
              :closable="!selectDisabled"
              :size="collapseTagSize"
              :hit="item.hitState"
              type="info"
              class="zm-tag-fix"
              @close="deleteTag($event, item)"
              disable-transitions
            >
              <span class="zm-select__tags-text">{{ item.currentLabel }}</span>
            </zm-tag>
          </div>
        </div>
        <button
          class="zm-select-clear"
          v-show="showClear"
          type="button"
          @click.stop="clearSelect"
          aria-label="Clear Select"
        >
          <i :class="['zm-select__caret', 'zm-input__icon', 'zm-icon-error','is-show-close']"></i>
        </button>
        <button
          tabindex="-1"
          class="zm-select-toggle"
          type="button"
          aria-hidden="true"
          :disabled="selectDisabled"
          @click.stop="handleIconClick"
          :aria-label="toggleText"
        >
          <i :class="['zm-select__caret', 'zm-input__icon', 'zm-icon-up']"></i>
        </button>
      </div>
    </zm-scrollbar>
    <transition
      name="zm-zoom-in-top"
      @after-enter="handleMenuEnter"
      @after-leave="handleMenuLeave"
      :duration="duration"
    >
      <zm-select-dropdown
        ref="popper"
        :class="{'zm-select-menu-width-limit': widthLimit}"
        :append-to-body="popperAppendToBody"
        :width-limit="widthLimit"
        :popper-options="popperOptions"
        v-show="visible"
      >
        <div class="zm-select-dropdown__wrap">
          <zm-tree
            :data="tree.data"
            :default-expanded-keys="tree.defaultExpandedKeys"
            :default-checked-keys="value"
            :check-strictly="checkStrictly"
            :props="tree.props || {}"
            :check-on-click-node="tree.checkOnClickNode || false"
            :single-choice="tree.singleChoice"
            :filter-node-method="filterNodeMethod"
            show-checkbox
            :highlight-current="highlightCurrent"
            node-key="id"
            ref="tree"
            @hook:mounted="handleTreeSelectChange"
            @check="handleTreeSelectChange"
            @keydown="handleTreeKeyDown"
            @node-expand="handleTreeExpandChange"
          >
          </zm-tree>
        </div>
      </zm-select-dropdown>
    </transition>
  </div>
</template>

<script>
  import SelectMixin from './select-mixin';
  import AriaUtils from '../../utils/aria-utils';
  import { valueEquals } from '../../utils/util';
  import debounce from 'throttle-debounce/debounce';

  const sizeMap = {
    'large': 38, // 加上2像素的边框厚度后为40px
    'small': 30
  };

  export default {
    name: 'TreeSelect',
    mixins: [SelectMixin],
    components: {
      ZmTree: () => import(/* webpackChunkName: 'zm-tree' */ '../tree')
    },
    props: {
      tree: Object,
      checkStrictly: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        query: ''
      };
    },
    computed: {
      highlightCurrent() {
        if (this.tree?.highlightCurrent !== undefined) {
          return this.tree.highlightCurrent;
        }
        return true;
      }
    },

    watch: {
      value(newVal, oldVal) {
        if (this.tree.singleChoice) {
          this.setSelected();
          this.query = this.selectedLabel;
        } else {
          if (newVal.length > 0) {
            this.currentPlaceholder = '';
          } else {
            this.currentPlaceholder = this.cachedPlaceHolder;
          }
          this.$refs.tree.setCheckedKeys(newVal);
          this.setSelected();

        }
        if (!valueEquals(newVal, oldVal)) {
          this.dispatch('ZmFormItem', 'el.form.change', newVal);
        }
      },
      visible(newVal) {
        this.resetHoverIndex();
        if (newVal) {
          this.handleIconShow();
          this.broadcast('ZmSelectDropdown', 'updatePopper');

          if (this.tree.singleChoice) {
            this.selectedLabel = this.selected?.[0]?.currentLabel || '';
          }

          if (this.tree.filterable) {
            this.$nextTick(() => {
              this.focus();
            });
          } else {
            // wait for the list show, for AX
            setTimeout(() => {
              this.focusList();
            }, 50);
          }

        } else {
          this.handleIconHide();
          this.broadcast('ZmSelectDropdown', 'destroyPopper');
          this.$emit('blur');
          this.dispatch('ZmFormItem', 'el.form.blur');

          if (this.tree.filterable) {
            this.setSelected();
            this.query = this.selectedLabel;
            this.$nextTick(() => {
              this.$refs.tree.filter('');
            });
          }

        }
        this.$emit('visible-change', newVal);
      }
    },
    methods: {
      focus() {
        this.$refs.referenceInner && this.$refs.referenceInner.focus();
      },
      blur() {
        this.$refs.referenceInner && this.$refs.referenceInner.blur();
      },
      filterNodeMethod(...args) {
        this.$nextTick(() => {
          this.broadcast('ZmSelectDropdown', 'updatePopper');
        });
        if (this.tree?.filterNodeMethod) {
          return this.tree.filterNodeMethod(...args);
        }
        return true;
      },
      resetInputHeight() {
        this.$nextTick(() => {
          if (!this.tree.filterable) {
            const sizeInMap = sizeMap[this.selectSize] || 30;
            this.$refs.referenceInner.style.height = this.selected.length === 0
              ? sizeInMap + 'px'
              : 0;
          }

          if (this.visible) {
            this.broadcast('ZmSelectDropdown', 'updatePopper');
          }
        });
      },
      handleQueryChange(val, userInput) {
        if (userInput && !this.visible && val) {
          this.visible = true;
        }

        this.$refs.tree.filter(val);
      },
      setSelected() {
        if (!this.$refs.tree) {
          return;
        }
        const result = [];
        const nodes = this.$refs.tree.getCheckedNodes(false, false, true);

        nodes.forEach((node) => {
          result.push({
            value: node.id,
            currentLabel: this.tree?.props?.label ? node[this.tree.props.label] : node.label
          });
        });
        this.selected = result;

        if (this.tree.singleChoice) {
          const node = nodes?.[0] || {};

          if (!node.pathId) {
            this.selectedLabel = node?.[this.tree.props.label] || '';
          } else {
            const pathIdArr = node?.pathId?.split(',') || [];
            let children = this.tree?.data || [];
            let fullPath = '';

            while (pathIdArr.length) {
              const id = pathIdArr.shift();
              const node = children?.find(item => item.id === id);

              fullPath += `${fullPath ? '/' : ''}${node?.[this.tree.props.label]}`;
              children = this.tree?.props?.children ? node[this.tree.props.children] : node.children;
            }

            this.selectedLabel = `${fullPath}/${node?.[this.tree.props.label]}`;
          }

        }

        this.resetInputHeight();
      },
      deleteTag(event, tag) {
        let index = this.selected.indexOf(tag);
        if (index > -1 && !this.selectDisabled) {
          const value = this.value.slice();
          value.splice(index, 1);
          this.$emit('input', value);
          this.emitChange(value);
          this.$emit('remove-tag', tag.value);
          this.$refs.tree.setCheckedKeys(value.map((v) => {
            return v.value;
          }));
          this.focus();
        }
        event.stopPropagation();
      },
      handleTreeSelectChange() {
        const nodes = this.$refs.tree.getCheckedNodes(false, false, true);
        const result = [];
        nodes.forEach(node => {
          result.push(node.id);
        });
        this.$emit('input', result);
        this.emitChange(result);

        if (this.tree.singleChoice && nodes.length === 1 && !nodes?.[0]?.subDispositionList?.length) {
          this.visible = false;
          this.focus();
        }
      },
      handleTreeExpandChange() {
        this.$nextTick(() => {
          this.broadcast('ZmSelectDropdown', 'updatePopper');
        });
      },
      handleBtnKeyDown(event) {
        if (this.selectDisabled) {
          return;
        }
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.enter:
          case keys.space:
          case keys.down:
            this.visible = true;
            event.preventDefault();
            event.stopPropagation();
            break;
        }
      },
      handleKeyDown(event) {
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.enter:
          case keys.space:
            this.visible = true;
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.esc:
            if (this.visible) {
              event.stopPropagation();
            }
            this.handleEsc();
            event.preventDefault();
            break;
          case keys.down:
            if (this.visible) {
              this.focusList();
            } else {
              this.visible = true;
            }
            event.preventDefault();
            event.stopPropagation();
            break;
        }
      },
      focusList() {
        const treeItems = this.$refs.tree.$el.querySelectorAll('.is-focusable[role=treeitem]');
        treeItems[0] && treeItems[0].focus();
      },
      handleTreeKeyDown(event) {
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;

        switch (keyCode) {
          case keys.esc:
            this.handleEsc();
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.tab:
            this.visible = false;
            event.preventDefault();
            this.$nextTick(() => {
              this.focus();
            });
            break;
        }
      }
    },
    created() {
      this.debouncedQueryChange = debounce(this.debounce, (e) => {
        this.handleQueryChange(e.target.value, true);
      });
    }
  };
</script>
